import { useEffect, useRef, useState } from 'react';

/**
 * Gates a "is loading" boolean to dampen three flicker classes:
 *
 *   - **Delayed-show** (`delayShowMs`, default 100ms): on a `false → true`
 *     transition that happens *after* mount, wait this long before flipping
 *     the gated value true. A load that resolves within the window never
 *     paints a skeleton at all. **Initial-mount exception**: if `loading`
 *     is already `true` when the hook first runs, the gated value is `true`
 *     immediately — this avoids a blank gap when a parent guard finishes
 *     and an adjacent child guard mounts mid-load.
 *   - **Min-visible** (`minVisibleMs`, default 300ms): once the gated value
 *     has flipped true, hold it true for at least this long even if the
 *     source flips false sooner.
 *   - **Hide-grace** (`hideGraceMs`, default 0): on a `true → false`
 *     transition, wait at least this long before flipping the gated value
 *     false — even if min-visible has already elapsed. Absorbs brief
 *     `loading=false` dips between sequential sources (e.g., the
 *     `LoadingSkeletonProvider` passes a non-zero value so a brief gap
 *     between guard handoffs doesn't blink the skeleton).
 *
 *   const showSkeleton = useFlickerGuard(isLoading);
 *   if (showSkeleton) return <LoadingSkeleton />;
 *   return children;
 *
 * Pass `0` for any threshold to disable that behavior. Defaults follow the
 * Material Design progress-indicator timing guidance.
 */
export function useFlickerGuard(
  loading: boolean,
  minVisibleMs = 300,
  delayShowMs = 100,
  hideGraceMs = 0,
): boolean {
  // Initial-mount exception: start true if loading is already true at mount,
  // so adjacent guards (auth check → workspace check) hand off the skeleton
  // continuously instead of producing a 100ms blank window between them.
  const [show, setShow] = useState(loading);
  const shownAtRef = useRef<number | null>(loading ? Date.now() : null);

  useEffect(() => {
    if (loading) {
      if (show) return; // already showing — nothing to schedule
      if (delayShowMs === 0) {
        shownAtRef.current = Date.now();
        setShow(true);
        return;
      }
      const t = setTimeout(() => {
        shownAtRef.current = Date.now();
        setShow(true);
      }, delayShowMs);
      return () => clearTimeout(t);
    }
    // loading === false
    if (!show) return; // never showed — nothing to do
    const elapsed = shownAtRef.current ? Date.now() - shownAtRef.current : 0;
    const remaining = Math.max(hideGraceMs, minVisibleMs - elapsed);
    if (remaining === 0) {
      shownAtRef.current = null;
      setShow(false);
      return;
    }
    const t = setTimeout(() => {
      shownAtRef.current = null;
      setShow(false);
    }, remaining);
    return () => clearTimeout(t);
  }, [loading, show, minVisibleMs, delayShowMs, hideGraceMs]);

  return show;
}
